import React from 'react';
import styled from 'styled-components';
import { FaFacebook, FaInstagram, FaWhatsapp, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import Icon from './Icon';

const FooterSection = styled.footer`
  background-color: #ff9d5c;
  color: #fff;
  padding: 4rem 0 2rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 2rem 0 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box;
  width: 100%;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const FooterColumn = styled.div`
  h3 {
    color: #fff;
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    position: relative;
    padding-bottom: 0.75rem;
    font-weight: 700;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 3px;
      background-color: #fff;
    }
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
    }
  }
  
  @media (max-width: 768px) {
    &:nth-child(2), &:nth-child(3) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
      
      h3 {
        grid-column: 1 / -1;
      }
    }
  }
`;

const AboutText = styled.p`
  color: #fff;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background-color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff9d5c;
  transition: all 0.3s ease;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: #2c3e50;
    color: #fff;
    transform: translateY(-3px);
    box-shadow: 0px 5px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterListItem = styled.li`
  margin-bottom: 1rem;
  
  a {
    color: #fff;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: color 0.3s ease;
    font-weight: 500;
    
    &:hover {
      color: #2c3e50;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
    
    a {
      font-size: 0.9rem;
    }
  }
`;

const ContactInfo = styled.div`
  p {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #fff;
    margin-bottom: 1rem;
    font-weight: 500;
    
    .icon-wrapper {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.2);
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      flex-shrink: 0;
    }
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      margin-bottom: 0.75rem;
      gap: 0.5rem;
      
      .icon-wrapper {
        width: 28px;
        height: 28px;
      }
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding-top: 1rem;
    font-size: 0.8rem;
  }
`;

const Footer: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <FooterColumn>
            <h3>Sobre Nós</h3>
            <AboutText>
              A Estima Pet é uma clínica veterinária comprometida com o bem-estar e saúde dos seus pets. 
              Oferecemos serviços de qualidade com uma equipe especializada e equipamentos modernos.
            </AboutText>
            <SocialLinks>
              <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <Icon icon={FaFacebook} size={20} />
              </SocialLink>
              <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <Icon icon={FaInstagram} size={20} />
              </SocialLink>
              <SocialLink href="https://wa.me/77999441369" target="_blank" rel="noopener noreferrer">
                <Icon icon={FaWhatsapp} size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>Links Rápidos</h3>
            <FooterList>
              <FooterListItem>
                <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Início</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#quem-somos" onClick={(e) => { e.preventDefault(); scrollToSection('quem-somos'); }}>Sobre Nós</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Serviços</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#contato" onClick={(e) => { e.preventDefault(); scrollToSection('contato'); }}>Contato</a>
              </FooterListItem>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <h3>Serviços</h3>
            <FooterList>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Consultas</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Vacinas</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Exames</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Castração</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Pet Shop</a>
              </FooterListItem>
              <FooterListItem>
                <a href="#servicos" onClick={(e) => { e.preventDefault(); scrollToSection('servicos'); }}>Cirurgias</a>
              </FooterListItem>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <h3>Contato</h3>
            <ContactInfo>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaMapMarkerAlt} size={16} />
                </span>
                Rua Risolene Lima Dias, 25A – Brumado-BA
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaPhone} size={16} />
                </span>
                (77) 99944-1369
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaEnvelope} size={16} />
                </span>
                clinicaestimapet@gmail.com
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaClock} size={16} />
                </span>
                Seg-Sex: 8h às 18h
              </p>
            </ContactInfo>
          </FooterColumn>
        </FooterContent>

        <Copyright>
          &copy; {new Date().getFullYear()} Estima Pet - Clínica Veterinária. Todos os direitos reservados.
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer; 